/**
 * ProjectDocumentLibrary Component
 *
 * Displays and manages documents for a development project.
 * Supports project-wide, unit-specific, and segment-specific documents.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import { useProjectDocuments } from '@/hooks/useProject';
import {
  Upload,
  FileText,
  Download,
  Trash2,
  Archive,
  MoreVertical,
  FolderOpen,
  Building2,
  Layers,
  Search,
  Plus,
  File,
  Image as ImageIcon,
  FileSpreadsheet,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import type {
  ProjectDocument,
  ProjectDocumentScope,
  ProjectDocumentCategory,
  Unit,
  ProjectSegment,
} from '@/types/project';
import { PROJECT_DOCUMENT_CATEGORY_LABELS } from '@/types/project';

interface ProjectDocumentLibraryProps {
  projectId: string;
  units: Unit[];
  segments: ProjectSegment[];
}

// Scope labels and icons
const SCOPE_CONFIG: Record<ProjectDocumentScope, { label: string; icon: typeof FileText }> = {
  project_wide: { label: 'Project-Wide', icon: FolderOpen },
  unit_specific: { label: 'Unit-Specific', icon: Building2 },
  segment_specific: { label: 'Segment/Phase', icon: Layers },
};

// File type icons
function getFileIcon(fileType: string) {
  if (fileType.startsWith('image/')) return ImageIcon;
  if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('csv')) {
    return FileSpreadsheet;
  }
  return FileText;
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Format date
function formatDate(date: any): string {
  if (!date) return 'Unknown';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ProjectDocumentLibrary({
  projectId,
  units,
  segments,
}: ProjectDocumentLibraryProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Document state
  const {
    documents,
    projectWideDocuments,
    unitDocuments,
    segmentDocuments,
    isLoading,
    uploadDocument,
    deleteDocument,
    archiveDocument,
  } = useProjectDocuments(projectId);

  // Local state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ProjectDocumentCategory | 'all'>('all');

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: 'other' as ProjectDocumentCategory,
    scope: 'project_wide' as ProjectDocumentScope,
    unitId: '',
    segmentId: '',
    file: null as File | null,
  });

  // Filter documents
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = doc.name.toLowerCase().includes(query);
        const matchesFileName = doc.fileName.toLowerCase().includes(query);
        const matchesTags = doc.tags?.some((tag) => tag.toLowerCase().includes(query));
        if (!matchesName && !matchesFileName && !matchesTags) return false;
      }
      // Category filter
      if (categoryFilter !== 'all' && doc.category !== categoryFilter) return false;
      return true;
    });
  }, [documents, searchQuery, categoryFilter]);

  // Handle file selection
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadForm((prev) => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, ''),
      }));
    }
  }, []);

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (!uploadForm.file || !uploadForm.name) {
      toast({
        title: 'Missing information',
        description: 'Please provide a file and name',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      await uploadDocument(
        {
          projectId,
          file: uploadForm.file,
          name: uploadForm.name,
          description: uploadForm.description || undefined,
          category: uploadForm.category,
          scope: uploadForm.scope,
          unitId: uploadForm.scope === 'unit_specific' ? uploadForm.unitId : undefined,
          segmentId: uploadForm.scope === 'segment_specific' ? uploadForm.segmentId : undefined,
        },
        (progress) => setUploadProgress(progress)
      );

      toast({
        title: 'Document uploaded',
        description: `${uploadForm.name} has been uploaded successfully`,
      });

      // Reset form
      setShowUploadDialog(false);
      setUploadForm({
        name: '',
        description: '',
        category: 'other',
        scope: 'project_wide',
        unitId: '',
        segmentId: '',
        file: null,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [uploadForm, projectId, uploadDocument, toast]);

  // Handle delete
  const handleDelete = useCallback(async (doc: ProjectDocument) => {
    try {
      await deleteDocument(doc.id);
      toast({
        title: 'Document deleted',
        description: `${doc.name} has been deleted`,
      });
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Delete failed',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
    }
  }, [deleteDocument, toast]);

  // Handle archive
  const handleArchive = useCallback(async (doc: ProjectDocument) => {
    try {
      await archiveDocument(doc.id);
      toast({
        title: 'Document archived',
        description: `${doc.name} has been archived`,
      });
    } catch (err) {
      console.error('Archive error:', err);
      toast({
        title: 'Archive failed',
        description: 'Failed to archive document',
        variant: 'destructive',
      });
    }
  }, [archiveDocument, toast]);

  // Document row component
  const DocumentRow = ({ doc }: { doc: ProjectDocument }) => {
    const FileIcon = getFileIcon(doc.fileType);

    return (
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="p-2 bg-background rounded-lg">
            <FileIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">{doc.name}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{formatFileSize(doc.fileSize)}</span>
              <span>•</span>
              <span>{formatDate(doc.createdAt)}</span>
              <Badge variant="outline" className="text-xs">
                {PROJECT_DOCUMENT_CATEGORY_LABELS[doc.category] || doc.category}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {doc.downloadUrl && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(doc.downloadUrl, '_blank')}
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {doc.downloadUrl && (
                <DropdownMenuItem onClick={() => window.open(doc.downloadUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in new tab
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleArchive(doc)}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDelete(doc)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Document Library
              </CardTitle>
              <CardDescription>
                {documents.length} document{documents.length !== 1 ? 's' : ''} in this project
              </CardDescription>
            </div>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={categoryFilter}
              onValueChange={(value) => setCategoryFilter(value as ProjectDocumentCategory | 'all')}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(PROJECT_DOCUMENT_CATEGORY_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Documents by Scope */}
          {documents.length === 0 ? (
            <div className="text-center py-8">
              <File className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Documents Yet</h3>
              <p className="text-muted-foreground mb-4">
                Upload project documents like plans, permits, and specifications.
              </p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Upload First Document
              </Button>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No documents match your search.</p>
              <Button
                variant="link"
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('all');
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={['project_wide']} className="w-full">
              {/* Project-Wide Documents */}
              <AccordionItem value="project_wide">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    <span>Project-Wide Documents</span>
                    <Badge variant="secondary" className="ml-2">
                      {projectWideDocuments.filter((d) =>
                        filteredDocuments.some((fd) => fd.id === d.id)
                      ).length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pt-2">
                    {projectWideDocuments
                      .filter((d) => filteredDocuments.some((fd) => fd.id === d.id))
                      .map((doc) => (
                        <DocumentRow key={doc.id} doc={doc} />
                      ))}
                    {projectWideDocuments.filter((d) =>
                      filteredDocuments.some((fd) => fd.id === d.id)
                    ).length === 0 && (
                      <p className="text-sm text-muted-foreground py-2">
                        No project-wide documents
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Unit-Specific Documents */}
              {units.length > 0 && (
                <AccordionItem value="unit_specific">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      <span>Unit-Specific Documents</span>
                      <Badge variant="secondary" className="ml-2">
                        {Array.from(unitDocuments.values())
                          .flat()
                          .filter((d) => filteredDocuments.some((fd) => fd.id === d.id)).length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      {units.map((unit) => {
                        const unitDocs = unitDocuments.get(unit.id) || [];
                        const filteredUnitDocs = unitDocs.filter((d) =>
                          filteredDocuments.some((fd) => fd.id === d.id)
                        );
                        if (filteredUnitDocs.length === 0) return null;
                        return (
                          <div key={unit.id}>
                            <p className="text-sm font-medium text-muted-foreground mb-2">
                              {unit.label}
                            </p>
                            <div className="space-y-2 pl-4">
                              {filteredUnitDocs.map((doc) => (
                                <DocumentRow key={doc.id} doc={doc} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {Array.from(unitDocuments.values())
                        .flat()
                        .filter((d) => filteredDocuments.some((fd) => fd.id === d.id)).length ===
                        0 && (
                        <p className="text-sm text-muted-foreground py-2">
                          No unit-specific documents
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Segment-Specific Documents */}
              {segments.length > 0 && (
                <AccordionItem value="segment_specific">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      <span>Segment/Phase Documents</span>
                      <Badge variant="secondary" className="ml-2">
                        {Array.from(segmentDocuments.values())
                          .flat()
                          .filter((d) => filteredDocuments.some((fd) => fd.id === d.id)).length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      {segments.map((segment) => {
                        const segDocs = segmentDocuments.get(segment.id) || [];
                        const filteredSegDocs = segDocs.filter((d) =>
                          filteredDocuments.some((fd) => fd.id === d.id)
                        );
                        if (filteredSegDocs.length === 0) return null;
                        return (
                          <div key={segment.id}>
                            <p className="text-sm font-medium text-muted-foreground mb-2">
                              {segment.name}
                            </p>
                            <div className="space-y-2 pl-4">
                              {filteredSegDocs.map((doc) => (
                                <DocumentRow key={doc.id} doc={doc} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {Array.from(segmentDocuments.values())
                        .flat()
                        .filter((d) => filteredDocuments.some((fd) => fd.id === d.id)).length ===
                        0 && (
                        <p className="text-sm text-muted-foreground py-2">
                          No segment-specific documents
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Add a document to the project library.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* File Input */}
            <div>
              <Label htmlFor="file">File *</Label>
              <div className="mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  id="file"
                  className="block w-full text-sm text-muted-foreground
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-primary file:text-primary-foreground
                    hover:file:bg-primary/90
                    cursor-pointer"
                  onChange={handleFileSelect}
                />
              </div>
              {uploadForm.file && (
                <p className="text-sm text-muted-foreground mt-1">
                  {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                </p>
              )}
            </div>

            {/* Document Name */}
            <div>
              <Label htmlFor="name">Document Name *</Label>
              <Input
                id="name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter document name"
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={uploadForm.category}
                onValueChange={(value: ProjectDocumentCategory) =>
                  setUploadForm((prev) => ({ ...prev, category: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PROJECT_DOCUMENT_CATEGORY_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scope */}
            <div>
              <Label htmlFor="scope">Scope</Label>
              <Select
                value={uploadForm.scope}
                onValueChange={(value: ProjectDocumentScope) =>
                  setUploadForm((prev) => ({ ...prev, scope: value, unitId: '', segmentId: '' }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="project_wide">Project-Wide</SelectItem>
                  {units.length > 0 && <SelectItem value="unit_specific">Unit-Specific</SelectItem>}
                  {segments.length > 0 && (
                    <SelectItem value="segment_specific">Segment/Phase-Specific</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Unit Selection */}
            {uploadForm.scope === 'unit_specific' && units.length > 0 && (
              <div>
                <Label htmlFor="unit">Unit *</Label>
                <Select
                  value={uploadForm.unitId}
                  onValueChange={(value) => setUploadForm((prev) => ({ ...prev, unitId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Segment Selection */}
            {uploadForm.scope === 'segment_specific' && segments.length > 0 && (
              <div>
                <Label htmlFor="segment">Segment *</Label>
                <Select
                  value={uploadForm.segmentId}
                  onValueChange={(value) =>
                    setUploadForm((prev) => ({ ...prev, segmentId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a segment" />
                  </SelectTrigger>
                  <SelectContent>
                    {segments.map((segment) => (
                      <SelectItem key={segment.id} value={segment.id}>
                        {segment.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={uploadForm.description}
                onChange={(e) =>
                  setUploadForm((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Brief description of the document"
                rows={2}
              />
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Uploading...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUploadDialog(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={isUploading || !uploadForm.file}>
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
